import React, { useState } from 'react';
import { Check, X, Star, ShieldCheck, ArrowRight } from 'lucide-react';
import MarketingLayout from '../components/MarketingLayout';
import { Link } from 'react-router-dom';
import { usePricingPlans } from '../hooks/usePricingPlans';
import { motion, AnimatePresence } from 'framer-motion';
export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const { plans, accelerators: ACCELERATORS, loading } = usePricingPlans();

  const billingOptions = [
      { id: 'monthly', label: 'Monthly', sublabel: 'Pay month by month' },
      { id: '3month', label: '3-Month Accelerator', sublabel: 'Save up to 10%', tag: 'SAVE' },
      { id: '6month', label: '6-Month Accelerator', sublabel: 'Save up to 17% · Free month', tag: 'BEST' },
  ];

  const renderPlanCard = (plan, index) => {
      const isEnterprise = plan.id === 'enterprise';

      const accelerator = ACCELERATORS.find(a => a.id === `${plan.id}_${billingCycle === '3month' ? '3' : '6'}_month`);
      const showAsAcc = billingCycle !== 'monthly' && accelerator;

      // Higher index = higher price. We make the box bigger by scaling its vertical padding and pulling it up slightly.
      const sizeClasses = [
        'py-8 mt-6',     // index 0 (Starter)
        'py-10 mt-4',    // index 1 (Growth)
        'py-12 mt-2',    // index 2 (Pro)
        'py-14 mt-0'     // index 3 (Enterprise)
      ][index] || 'py-8 mt-6';

      return (
          <div className={`relative bg-white border rounded-[32px] px-7 flex flex-col h-full transition-colors duration-300 hover:-translate-y-2 shadow-xl shadow-slate-200/50 ${sizeClasses}
              ${plan.is_popular ? 'border-primary ring-2 ring-primary/20 z-10' : 'border-slate-100'}`}
          >
              {plan.is_popular && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-[#1A1A1A] font-black text-[10px] uppercase tracking-[0.2em] px-5 py-1.5 rounded-full shadow-sm">
                      🔥 Flash Sale
                  </div>
              )}

              <div className="mb-5">
                  <h2 className="text-xl font-black text-charcoal tracking-tight">{plan.name}</h2>
                  <p className="text-slate-400 text-[11px] font-bold mt-1 min-h-[32px] leading-relaxed">{plan.target_audience}</p>
              </div>

              <div className="mb-6">
                  {showAsAcc ? (
                      <>
                          <div className="flex items-baseline gap-2">
                              <span className="text-5xl font-black text-charcoal tracking-tighter">{accelerator.upfront_price}</span>
                              <span className="text-sm text-slate-400 font-bold uppercase tracking-widest">Total</span>
                          </div>
                          <div className="flex flex-col mt-3 gap-1.5 border-l-2 border-primary/30 pl-3">
                              <div className="text-xs font-black text-green-600 uppercase tracking-widest">{accelerator.savings_text}</div>
                              <div className="text-sm text-slate-500 font-medium">
                                  Effectively <strong className="text-charcoal font-black">{accelerator.effective_monthly}</strong> (vs <span className="line-through">{plan.price}/mo</span>)
                              </div>
                          </div>
                      </>
                  ) : (
                      <div className="flex items-baseline text-charcoal">
                          <span className="text-5xl font-black tracking-tighter">{plan.price}</span>
                          <span className="text-slate-400 ml-1 font-bold text-sm tracking-widest uppercase">/mo</span>
                      </div>
                  )}
              </div>

              <div className="flex flex-col gap-2 mb-6">
                  <Link
                      to="/login"
                      className={`w-full py-4 font-black text-sm uppercase tracking-widest transition-colors flex items-center justify-center gap-2 rounded-2xl
                          ${isEnterprise ? 'bg-[#1A1A1A] text-primary hover:bg-slate-800 active:scale-95' :
                          'bg-primary text-[#1A1A1A] hover:bg-[#F0C900] hover:shadow-lg hover:shadow-primary/30 active:scale-95'}`}
                  >
                      {plan.cta_text} →
                  </Link>
              </div>

              <div className="flex-1 space-y-3">
                  {(plan.features || plan.plan_features || []).map((feature, i) => (
                      <div key={i} className={`flex items-start gap-2.5 ${feature.included ? 'text-slate-700' : 'text-slate-300'}`}>
                          <span className={`material-symbols-outlined text-lg shrink-0 ${feature.included ? 'text-green-500' : 'text-slate-200'}`}>
                              {feature.included ? 'check_circle' : 'cancel'}
                          </span>
                          <span className={`text-xs leading-relaxed ${feature.included ? 'font-medium' : 'line-through'}`}>{feature.text}</span>
                      </div>
                  ))}
              </div>
          </div>
      );
  };

  return (
    <MarketingLayout>
      <div className="bg-[#FCFAF5] min-h-screen">
        <section className="mx-auto max-w-7xl px-4 py-20 text-center lg:px-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-xs font-black uppercase tracking-widest mb-6">
            <Star size={12} fill="currentColor" />
            Pricing Plans
          </div>
          <h1 className="text-5xl lg:text-7xl font-black tracking-tight text-charcoal mb-6">
            Grow your revenue, <br /> 
            <span className="text-primary italic">not your expenses.</span>
          </h1>
          <p className="text-lg text-slate-500 font-medium max-w-2xl mx-auto mb-12 leading-relaxed">
            Choose the right plan for your business stage. From small food trucks to multi-outlet brands, we have you covered.
          </p>

          {/* View Toggle */}
          <div className="flex justify-center mb-16">
            <div className="bg-white p-2 rounded-full border border-slate-200 flex items-center shadow-sm max-w-full overflow-x-auto">
              {billingOptions.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setBillingCycle(opt.id)}
                  className={`px-6 py-3 rounded-full text-sm font-black transition-all whitespace-nowrap flex items-center gap-2 ${
                    billingCycle === opt.id 
                      ? 'bg-primary text-[#1A1A1A] shadow-md' 
                      : 'text-slate-500 hover:text-charcoal hover:bg-slate-50'
                  }`}
                >
                  {opt.label}
                  {opt.tag && <span className={`text-[10px] px-2 py-0.5 rounded-full ${billingCycle === opt.id ? 'bg-white/50' : 'bg-slate-100 text-slate-600'}`}>{opt.tag}</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Grid Layout */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <motion.div layout className={`grid grid-cols-1 md:grid-cols-2 gap-5 items-start max-w-7xl mx-auto mb-10 text-left ${billingCycle !== 'monthly' ? 'lg:max-w-4xl' : 'lg:grid-cols-4'}`}>
              <AnimatePresence mode="popLayout">
                  {plans.filter(p => billingCycle === 'monthly' || (p.id === 'pro' || p.id === 'enterprise'))
                      .map((plan, index) => (
                          <motion.div
                              key={plan.id}
                              layout
                              initial={{ opacity: 0, scale: 0.9, y: 20 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.8, filter: "blur(4px)" }}
                              transition={{ duration: 0.4, type: "spring", bounce: 0.2 }}
                              className="h-full"
                          >
                              {renderPlanCard(plan, index)}
                          </motion.div>
                      ))}
              </AnimatePresence>
            </motion.div>
          )}
        </section>

        {/* FAQ */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <h2 className="text-4xl font-black text-center mb-16">Frequently Asked Questions</h2>
            <div className="grid gap-6">
              {[
                ["How do I get my QR codes?", "Once you sign up and set your tables, you can download high-res QR PDFs directly from your dashboard to print immediately."],
                ["Can I upgrade my plan later?", "Absolutely. You can switch plans at any time from the Subscription page in your admin dashboard. Your new features will unlock instantly."],
                ["Does this work for multiple cafes?", "Our Enterprise plan is specially built for multi-outlet management, allowing you to manage chains from a single dashboard."]
              ].map(([q, a]) => (
                <div key={q} className="p-8 bg-[#FCFAF5] rounded-3xl border border-slate-100">
                  <h3 className="font-black text-xl text-charcoal mb-3">{q}</h3>
                  <p className="text-sm font-medium text-slate-500 leading-relaxed tracking-wide">{a}</p>
                </div>
              ))}
            </div>
            
            <div className="mt-16 bg-charcoal rounded-[3rem] p-10 text-center text-white relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full bg-primary/5 pattern-grid-lg opacity-20" />
                <h3 className="text-3xl font-black mb-4 relative z-10">Still have questions?</h3>
                <p className="text-white/60 mb-8 relative z-10">We're here to help you get your restaurant digitised in minutes.</p>
                <Link to="/contact" className="btn-primary inline-flex items-center gap-2 relative z-10">
                    Contact Support <ArrowRight size={18} />
                </Link>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}
